import { useMemo, useCallback, useRef, useEffect } from 'react';
import { useAnalyticsConfig, useAllAnalytics } from '../hooks';
import type { AnalyticsParameters, RelativeTimePeriod, WeavableTheme } from '../types';
import { themeToStyles } from '../utils';
import { VisualizationRenderer } from './visualizations';

/**
 * Props for WeavableAnalytics component
 */
export interface WeavableAnalyticsProps {
    /** Optional className for custom styling */
    className?: string;
    /** Optional message to display when no analytics are available */
    noAnalyticsMessage?: string;
    /** Optional callback fired when analytics are successfully loaded */
    onAnalyticsLoaded?: (params: { timePeriod: RelativeTimePeriod }) => void;
    /** Optional callback fired when an error occurs during config or analytics fetching */
    onError?: (error: Error) => void;
    /** Optional time period override (if not provided, uses config default) */
    timePeriod?: RelativeTimePeriod;
    /** Include delta comparison with the previous period (if not provided, uses config default) */
    includeDelta?: boolean;
    /** Optional preset to filter which analytics are returned by the config endpoint */
    preset?: string;
    /** Optional filter to specific analytics IDs */
    analyticsIds?: string[];
    /**
     * Optional per-insight parameter overrides, keyed by analyticsId. Each value is a flat
     * map of the insight's declared parameter keys (e.g. `lookAheadDays`) to overrides.
     * Only values keyed under a given analyticsId are sent to that insight's call, so keys
     * cannot leak across insights.
     *
     * @example
     * {
     *   growth_opportunities: { lookAheadDays: 30, capacityThreshold: 85 },
     * }
     */
    parameters?: AnalyticsParameters;
    /** Layout direction for analytics cards (default: 'vertical')
     * - 'vertical': cards stacked vertically
     * - 'horizontal': fixed column grid (column count set via analyticsColumns theme variable, default 4)
     * - 'horizontal-fit': cards share a single row equally, automatically fitting all cards
     */
    layout?: 'vertical' | 'horizontal' | 'horizontal-fit';
    /** Optional theme overrides scoped to this component (overrides provider-level theme) */
    theme?: Partial<WeavableTheme>;
}

/**
 * Get user's timezone
 */
function getUserTimeZone(): string {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
}

/**
 * WeavableAnalytics - Main component that fetches and displays analytics visualizations
 */
export function WeavableAnalytics({
    className,
    noAnalyticsMessage,
    onAnalyticsLoaded,
    onError,
    timePeriod: timePeriodProp,
    includeDelta: includeDeltaProp,
    preset,
    analyticsIds,
    parameters,
    layout = 'vertical',
    theme,
}: WeavableAnalyticsProps) {
    // Convert theme overrides to CSS variables
    const themeStyles = useMemo(() => themeToStyles(theme), [theme]);
    // Store callbacks in refs to keep them up-to-date without causing re-renders
    const onAnalyticsLoadedRef = useRef(onAnalyticsLoaded);
    const onErrorRef = useRef(onError);

    useEffect(() => {
        onAnalyticsLoadedRef.current = onAnalyticsLoaded;
        onErrorRef.current = onError;
    }, [onAnalyticsLoaded, onError]);

    // Create stable callback wrapper that never changes identity
    const stableOnAnalyticsLoaded = useCallback((params: { timePeriod: RelativeTimePeriod }) => {
        onAnalyticsLoadedRef.current?.(params);
    }, []);

    // Step 1: Fetch analytics config
    const {
        configs,
        timePeriod,
        includeDelta: configIncludeDelta,
        loading: configLoading,
        error: configError,
    } = useAnalyticsConfig(preset);

    // Filter configs if analyticsIds provided
    const filteredConfigs = useMemo(() => {
        if (!analyticsIds || analyticsIds.length === 0) return configs;
        return configs.filter((c) => analyticsIds.includes(c.id));
    }, [configs, analyticsIds]);

    // Use prop values if provided, otherwise use config defaults
    const effectiveTimePeriod = timePeriodProp ?? timePeriod;
    const effectiveIncludeDelta = includeDeltaProp ?? configIncludeDelta;

    const userIanaZone = useMemo(() => getUserTimeZone(), []);

    // Step 2: Fetch all analytics (no context needed - data is fetched directly from connector)
    const {
        analytics,
        loading: analyticsLoading,
        error: analyticsError,
    } = useAllAnalytics(
        filteredConfigs,
        effectiveTimePeriod,
        userIanaZone,
        effectiveIncludeDelta,
        filteredConfigs.length > 0,
        parameters,
    );

    // Filter to only analytics with valid visualizations (type guard ensures visualization is non-null)
    const validAnalytics = analytics.filter(
        (a): a is typeof a & { visualization: NonNullable<typeof a.visualization> } =>
            a.visualization !== null && !a.error,
    );

    // Notify when loaded - only fires when there are renderable visualizations
    useEffect(() => {
        if (validAnalytics.length > 0 && !analyticsLoading) {
            stableOnAnalyticsLoaded({ timePeriod: effectiveTimePeriod });
        }
    }, [validAnalytics.length, analyticsLoading, effectiveTimePeriod, stableOnAnalyticsLoaded]);

    // Handle errors
    const error = configError || analyticsError;

    useEffect(() => {
        if (error) {
            onErrorRef.current?.(error);
        }
    }, [error]);

    // Show loading state while fetching config or analytics
    if (configLoading || analyticsLoading) {
        return (
            <div className={`weavable-analytics ${className || ''}`.trim()} style={themeStyles}>
                <div className="weavable-section">
                    <div className="weavable-card weavable-skeleton">
                        <div className="weavable-card-content">
                            <div className="weavable-skeleton-line"></div>
                            <div className="weavable-skeleton-line"></div>
                            <div className="weavable-skeleton-line" style={{ width: '60%' }}></div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Show no UI on error - the error is propagated via the onError callback
    if (error) {
        return null;
    }

    // Show analytics - each visualization is a card within a section
    const layoutClass =
        layout === 'horizontal'
            ? 'weavable-analytics--horizontal'
            : layout === 'horizontal-fit'
              ? 'weavable-analytics--horizontal-fit'
              : '';
    const classes = ['weavable-analytics', layoutClass, className].filter(Boolean).join(' ');

    return (
        <div className={classes} style={themeStyles}>
            <div className="weavable-section">
                {validAnalytics.map(({ config, visualization }) => (
                    <div
                        key={config.id}
                        className={`weavable-card${visualization.type === 'table' ? ' weavable-card--full-width' : ''}`}
                    >
                        <VisualizationRenderer visualization={visualization} />
                    </div>
                ))}
                {validAnalytics.length === 0 && (
                    <div className="weavable-empty">
                        <p>{noAnalyticsMessage || 'No analytics available.'}</p>
                    </div>
                )}
            </div>
        </div>
    );
}
